import { User } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { CountrySelector } from '@/components/ui/country-selector';
import { DateInput } from '@/components/ui/date-input';
import type { InvestorType, KycStatus, AmlStatus } from '@/types/investor';
import {
  INVESTOR_TYPES,
  KYC_STATUS_OPTIONS,
  AML_STATUS_OPTIONS,
} from '@/types/investor';

interface InvestorProfileFormProps {
  formData: {
    name: string;
    investor_type: InvestorType;
    legal_form: string;
    country: string;
    address_line_1: string;
    address_line_2: string;
    city: string;
    postcode: string;
    tax_id: string;
    date_of_incorporation: string;
    kyc_status: KycStatus | '';
    aml_status: AmlStatus | '';
  };
  errors: Record<string, string>;
  onInputChange: (field: string, value: string) => void;
  onClearError: (field: string) => void;
}

export function InvestorProfileForm({
  formData,
  errors,
  onInputChange,
  onClearError,
}: InvestorProfileFormProps) {
  // Local handler that clears errors and calls parent's onInputChange
  const handleInputChange = (field: string, value: string) => {
    onInputChange(field, value);

    // Clear error when user starts typing
    if (errors[field]) {
      onClearError(field);
    }
  };
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center space-x-2">
          <User className="h-5 w-5" />
          <span>Investor Profile</span>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-2">
            <Label htmlFor="name">Investor Name *</Label>
            <Input
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              placeholder="e.g. Pension Fund Alpha"
              className={errors.name ? 'border-red-500' : ''}
            />
            {errors.name && (
              <p className="text-sm text-red-600">{errors.name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="investor_type">Investor Type *</Label>
            <Select
              value={formData.investor_type}
              onValueChange={(value) =>
                handleInputChange('investor_type', value)
              }
            >
              <SelectTrigger
                id="investor_type"
                className={errors.investor_type ? 'border-red-500' : ''}
              >
                <SelectValue placeholder="Select investor type" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(INVESTOR_TYPES).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.investor_type && (
              <p className="text-sm text-red-600">{errors.investor_type}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="legal_form">Legal Form</Label>
            <Input
              id="legal_form"
              value={formData.legal_form}
              onChange={(e) => handleInputChange('legal_form', e.target.value)}
              placeholder="e.g. Pension Fund, Foundation"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="date_of_incorporation">Date of Incorporation</Label>
            <DateInput
              id="date_of_incorporation"
              value={formData.date_of_incorporation}
              onChange={(value) =>
                handleInputChange('date_of_incorporation', value)
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="country">Country</Label>
            <CountrySelector
              id="country"
              value={formData.country}
              onValueChange={(value) => handleInputChange('country', value)}
              placeholder="Select country"
              className={errors.country ? 'border-red-500' : ''}
            />
            {errors.country && (
              <p className="text-sm text-red-600">{errors.country}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="tax_id">Tax ID</Label>
            <Input
              id="tax_id"
              value={formData.tax_id}
              onChange={(e) => handleInputChange('tax_id', e.target.value)}
              placeholder="e.g. NL123456789B01"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address_line_1">Address Line 1</Label>
            <Input
              id="address_line_1"
              value={formData.address_line_1}
              onChange={(e) =>
                handleInputChange('address_line_1', e.target.value)
              }
              placeholder="Street address"
            />
          </div>

          <div className="space-y-2 md:col-span-2">
            <Label htmlFor="address_line_2">Address Line 2</Label>
            <Input
              id="address_line_2"
              value={formData.address_line_2}
              onChange={(e) =>
                handleInputChange('address_line_2', e.target.value)
              }
              placeholder="Apartment, suite, etc."
            />
          </div>

          <div className="space-y-2 md:col-start-1">
            <Label htmlFor="city">City</Label>
            <Input
              id="city"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              placeholder="City"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="postcode">Postcode</Label>
            <Input
              id="postcode"
              value={formData.postcode}
              onChange={(e) => handleInputChange('postcode', e.target.value)}
              placeholder="Postcode"
            />
          </div>

          <div className="space-y-2 md:col-start-1">
            <Label htmlFor="kyc_status">KYC Status</Label>
            <Select
              value={formData.kyc_status}
              onValueChange={(value) => handleInputChange('kyc_status', value)}
            >
              <SelectTrigger
                id="kyc_status"
                className={errors.kyc_status ? 'border-red-500' : ''}
              >
                <SelectValue placeholder="Select KYC status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(KYC_STATUS_OPTIONS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.kyc_status && (
              <p className="text-sm text-red-600">{errors.kyc_status}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="aml_status">AML Status</Label>
            <Select
              value={formData.aml_status}
              onValueChange={(value) => handleInputChange('aml_status', value)}
            >
              <SelectTrigger
                id="aml_status"
                className={errors.aml_status ? 'border-red-500' : ''}
              >
                <SelectValue placeholder="Select AML status" />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(AML_STATUS_OPTIONS).map(([key, label]) => (
                  <SelectItem key={key} value={key}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.aml_status && (
              <p className="text-sm text-red-600">{errors.aml_status}</p>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
